const SIGN_UP_CODE_MESSAGES: Record<string, string> = {
    "auth/email-already-in-use": "The email address is already used by another account.",
    "auth/invalid-email": "The email address is not valid.",
    "auth/operation-not-allowed": "New signups are not presently enabled.",
    "auth/weak-password": "The password is too weak. Strengthen the password and try again.",
}

const SIGN_IN_CODE_MESSAGES: Record<string, string> = {
    "auth/invalid-credential": "The email address or password is incorrect.",
    "auth/invalid-email": "The email address is not registered. Please sign up.",
    "auth/user-disabled": "The user corresponding to the given email has been disabled.",
    "auth/user-not-found": "There is no user corresponding to the given email.",
    "auth/wrong-password": "The password is invalid for the given email.",
}

export function signUpFailureDisplayMessage(code: string | undefined, fallbackMessage: string): string {
    if (code && SIGN_UP_CODE_MESSAGES[code]) {
        return SIGN_UP_CODE_MESSAGES[code]
    }
    return fallbackMessage || "Unable to complete signup."
}

export function signInFailureDisplayMessage(code: string | undefined, fallbackMessage: string): string {
    if (code && SIGN_IN_CODE_MESSAGES[code]) {
        return SIGN_IN_CODE_MESSAGES[code]
    }
    return fallbackMessage
}

/**
 * Reads a verification-send reason from a callable error without mutating it.
 * Firebase Functions surfaces HttpsError `details` on `customData.details`.
 */
export function readVerificationSendReason(error: unknown): string | undefined {
    if (!error || typeof error !== "object") {
        return undefined
    }
    const record = error as {
        reason?: unknown
        details?: unknown
        customData?: {details?: unknown}
    }
    if (typeof record.reason === "string" && record.reason) {
        return record.reason
    }
    const details = record.customData?.details ?? record.details
    if (details && typeof details === "object" && "reason" in details) {
        const reason = (details as {reason?: unknown}).reason
        if (typeof reason === "string" && reason) {
            return reason
        }
    }
    return undefined
}

/**
 * A verify page with no action code is still waiting (login sends unverified
 * users here). Red failure is only for an action code that was rejected.
 */
export function shouldShowVerificationFailure(hadActionCode: boolean, verificationRejected: boolean): boolean {
    return hadActionCode && verificationRejected
}

/** Signup waits this long for the Auth onCreate allowlist before showing the verify screen. */
export const SIGNUP_VERIFICATION_WAIT_MS = 3000

/**
 * How long signup should wait for emailVerified to reach the client. The
 * profile ensure call verifies allowlisted addresses before it returns, so a
 * `false` answer means the address must use the email link and no wait is
 * needed. Only wait when the server said verified or the call failed.
 */
export function signupVerificationWaitBudgetMs(serverEmailVerified: boolean | undefined): number {
    return serverEmailVerified === false ? 0 : SIGNUP_VERIFICATION_WAIT_MS
}

export const SIGNUP_VERIFICATION_POLL_MS = 400

/**
 * Allowlisted signups become verified a moment after createUser returns.
 * Keep waiting until the budget is spent, then show the check-your-email screen.
 */
export function nextSignupVerificationStep(input: {
    emailVerified: boolean
    waitedMs: number
    waitBudgetMs: number
}): "continue" | "wait" | "show_verification_screen" {
    if (input.emailVerified) return "continue"
    if (input.waitedMs >= input.waitBudgetMs) return "show_verification_screen"
    return "wait"
}
